import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

// Selector for focusable elements that should have tabindex managed
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Sets tabindex="-1" on all focusable elements within an element to remove them from tab order.
 * @param {HTMLElement} element - The element containing focusable children
 */
function disableFocusableElements(element) {
  const focusableElements = element.querySelectorAll(FOCUSABLE_SELECTOR);
  focusableElements.forEach((el) => {
    el.setAttribute('tabindex', '-1');
  });
}

/**
 * @param {HTMLElement} element
 */
function lockFocusablesForMarqueeClone(element) {
  element.querySelectorAll(FOCUSABLE_SELECTOR).forEach((focusable) => {
    if (focusable.hasAttribute('data-marquee-tabsaved')) return;
    if (focusable.hasAttribute('tabindex')) {
      focusable.setAttribute('data-marquee-tabsaved', focusable.getAttribute('tabindex') ?? '');
    } else {
      focusable.setAttribute('data-marquee-tabsaved', 'native');
    }
    focusable.setAttribute('tabindex', '-1');
  });
}

/**
 * @param {HTMLElement} element
 */
function unlockFocusablesForMarqueeClone(element) {
  element.querySelectorAll('[data-marquee-tabsaved]').forEach((focusable) => {
    const saved = focusable.getAttribute('data-marquee-tabsaved');
    focusable.removeAttribute('data-marquee-tabsaved');
    if (saved === 'native') focusable.removeAttribute('tabindex');
    else if (saved != null) focusable.setAttribute('tabindex', saved);
  });
}

/**
 * Hides duplicate marquee strips from assistive tech (visual loop copies).
 * @param {HTMLElement} content
 */
function syncMarqueeRepeatedItemsAccessibility(content) {
  const children = [...content.children].filter(
    (c) => c instanceof HTMLElement && c.classList.contains('marquee__repeated-items')
  );

  children.forEach((child, index) => {
    if (index === 0) {
      child.removeAttribute('aria-hidden');
      unlockFocusablesForMarqueeClone(child);
    } else {
      child.setAttribute('aria-hidden', 'true');
      lockFocusablesForMarqueeClone(child);
    }
  });
}

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  /** @type {boolean} */
  #userPaused = false;

  /** @type {MediaQueryList} */
  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {(e: MediaQueryListEvent) => void} */
  #onReducedMotionChange = (event) => {
    if (event.matches) {
      this.setAttribute('data-disabled', '');
    } else if (!this.#userPaused) {
      this.removeAttribute('data-disabled');
    }
    this.#syncPauseButton();
  };

  connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;
    if (content.firstElementChild?.children.length === 0) return;

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();
    this.#syncRepeatedItemsA11y();

    if (this.#reducedMotionQuery.matches) {
      this.setAttribute('data-disabled', '');
    }
    this.#reducedMotionQuery.addEventListener('change', this.#onReducedMotionChange);

    const pauseButton = this.refs.pauseButton;
    if (pauseButton instanceof HTMLButtonElement) {
      pauseButton.addEventListener('click', this.#toggleUserPause);
    }
    this.#syncPauseButton();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reducedMotionQuery.removeEventListener('change', this.#onReducedMotionChange);
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  #syncRepeatedItemsA11y() {
    const { content } = this.refs;
    if (!(content instanceof HTMLElement)) return;
    syncMarqueeRepeatedItemsAccessibility(content);
  }

  #toggleUserPause = () => {
    this.#userPaused = !this.#userPaused;
    if (this.#userPaused || this.#reducedMotionQuery.matches) {
      this.setAttribute('data-disabled', '');
    } else {
      this.removeAttribute('data-disabled');
    }
    this.#syncPauseButton();
  };

  #syncPauseButton() {
    const pauseButton = this.refs.pauseButton;
    if (!(pauseButton instanceof HTMLButtonElement)) return;

    const paused = this.hasAttribute('data-disabled');
    const pauseLabel = this.dataset.pauseLabel || 'Pause scrolling banner';
    const playLabel = this.dataset.playLabel || 'Play scrolling banner';

    pauseButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
    pauseButton.setAttribute('aria-label', paused ? playLabel : pauseLabel);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const marqueeWidth = this.offsetWidth;
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return speed;
  }

  #handleResize = debounce(() => {
    const { content } = this.refs;
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
    this.#syncRepeatedItemsA11y();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    // Remove focusable elements from tab order since content is hidden from screen readers
    disableFocusableElements(clone);

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const wrapper = content.firstElementChild;

    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = wrapper.cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth =
      content.firstElementChild instanceof HTMLElement ? content.firstElementChild.offsetWidth : 1;

    return marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
